import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CalendarDays } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';
import { PostBody } from '../journal/PostBody';
import { getPostBySlug, type Post } from '../journal/posts';
const SITE_URL = 'https://www.groundedlivermore.com';
const SHARE_IMAGE = `${SITE_URL}/assets/og-grounded.jpg`;
function buildArticleSchema(post: Post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    image: SHARE_IMAGE,
    datePublished: post.datePublished,
    dateModified: post.datePublished,
    author: {
      '@type': 'Organization',
      name: post.author,
      url: SITE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: 'Grounded',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: SHARE_IMAGE
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/journal/${post.slug}`
    }
  };
}
export function JournalPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  if (!post) {
    return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-440" data-mp="ryv9bp5z5pc4">
        <SEO title="Post Not Found | Grounded Journal" description="This journal post could not be found. Browse the Grounded journal for stories on coffee, community, and life in Livermore." canonical={`${SITE_URL}/journal`} data-id="element-441" data-mp="4alha5i645cj" />
        <HamburgerMenu data-id="element-442" data-mp="96iw27ds93n7" />

        <main className="pt-40 pb-32 px-6 text-center max-w-xl mx-auto" data-id="element-443" data-mp="96alwn91ocfp">
          <h1 className="font-bebas text-5xl md:text-6xl mb-6" data-id="element-444" data-mp="1onaxbgtfrqn4">
            We Couldn't Find That Post
          </h1>
          <p className="font-inter text-lg text-white/60 mb-10 leading-relaxed" data-id="element-445" data-mp="1km7ohe9t2uve">
            It may have moved, or the link might have a typo. Have a look
            through the journal instead.
          </p>
          <Link to="/journal" className="inline-flex items-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors" data-id="element-446" data-mp="m93h21htpawr">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-447" data-mp="v99y1xcqct0o" />
            BACK TO THE JOURNAL
          </Link>
        </main>
      </div>;
  }
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-448" data-mp="3lhz4ltxsyh9">
      <SEO title={post.metaTitle} description={post.metaDescription} canonical={`${SITE_URL}/journal/${post.slug}`} structuredData={buildArticleSchema(post)} data-id="element-449" data-mp="7kz31atptnkb" />
      <HamburgerMenu data-id="element-450" data-mp="3bz73jvg7u4my" />

      <main data-id="element-451" data-mp="3bzzz009q57ps">
        <article className="pt-32 pb-20 px-6 md:px-12 lg:px-24 max-w-3xl mx-auto" data-id="element-452" data-mp="3by5olfw2wxzy">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-453" data-mp="3g00pija3l9d7">
            <Link to="/journal" className="inline-flex items-center gap-2 font-inter text-sm text-white/40 hover:text-[#D97E3A] transition-colors mb-10" data-id="element-454" data-mp="33kj43lioxxl7">
              <ArrowLeft size={16} aria-hidden="true" data-id="element-455" data-mp="2uhrqbf2cepqe" />
              Journal
            </Link>

            <h1 className="font-bebas text-5xl md:text-7xl leading-[0.92] mb-6" data-id="element-456" data-mp="2pki1o1ms2i3m">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-inter text-sm text-[#D97E3A] font-medium mb-12 pb-8 border-b border-white/10" data-id="element-457" data-mp="2krf9nxxnfgpj">
              <CalendarDays size={16} aria-hidden="true" data-id="element-458" data-mp="2bsux7eiom39r" />
              <time dateTime={post.datePublished} data-id="element-459" data-mp="2bq8qfh3m7rsl">{post.date}</time>
              <span className="text-white/20" aria-hidden="true" data-id="element-460" data-mp="26sfkmp684zsu">
                •
              </span>
              <span data-id="element-461" data-mp="21udz7xlbsoe7">By {post.author}</span>
            </div>

            <PostBody blocks={post.body} data-id="element-462" data-mp="5qhejds3cmh2" />

            {post.byline && <p className="font-inter text-base text-white/40 italic mt-14 pt-8 border-t border-white/10" data-id="element-463" data-mp="apu9ymx131v9">
                {post.byline}
              </p>}
          </motion.div>
        </article>

        <NewsletterSignup data-id="element-464" data-mp="zr77v03u9jq" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-465" data-mp="yzgoyld720b">
          <p className="font-bebas text-2xl text-white/50" data-id="element-466" data-mp="3j54bntx0a52i">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}